/**
 * ============================================================================
 * RAPOR EKRANI — J şeridi. Aşama 3'ün kullanıcı yüzü.
 * ============================================================================
 *
 * Bu ekranın tek işi motorun ürettiği `PortfolioReport`u GÖSTERMEK. Burada
 * hiçbir hesap yapılmıyor: toplama, çevirme, oranlama yok. Sebebi Aşama 3
 * planının 6. maddesi — bir sayı, dayandığı ölçümün tarihini söylemeden
 * gösterilemez — ve o yasağın uygulandığı yer motor. Arayüzde ikinci bir
 * aritmetik başlarsa damga ile sayı ayrışır: ekranda "09.09 fiyatıyla" yazan
 * bir satırın altındaki toplam başka bir şeyden çıkmış olur.
 *
 * ----------------------------------------------------------------------------
 * ÜÇ KURAL — EKRANIN TAMAMI BUNLARIN UYGULANMASI
 * ----------------------------------------------------------------------------
 *
 * 1. HİÇBİR `null` SIFIR GİBİ GÖSTERİLMEZ. Motorun `null`u "ölçemedim"
 *    demek; "0" yazmak ölçülmemiş bir şeyi ölçülmüş göstermek olurdu. Boş
 *    hücre "—" ve YANINDA sebebi (damga) duruyor.
 *
 * 2. HER TUTAR DAMGASIYLA BİRLİKTE. `StampedAmount.asOf`/`staleDays` satırın
 *    altında, dayanakların dökümü (`basis`) açılır bir bölümde. "Bu sayı
 *    nereden çıktı" sorusunun cevabı ekranda, ayrı bir yerde değil.
 *
 * 3. EKSİK TOPLAM SESSİZ KALMAZ. `complete: false` ise en üstte, toplamların
 *    ÜSTÜNDE uyarı var ve hangi pozisyonların toplama giremediği listeli.
 *    Toplamı gösterip eksikliği dipnota atmak, raporu olduğundan sağlam
 *    gösterirdi.
 *
 * ----------------------------------------------------------------------------
 * NİÇİN NAKİT AKIŞI DÖKÜMÜ SATIR SATIR
 * ----------------------------------------------------------------------------
 *
 * H1 temettü/faiz/masrafı getirinin bir bileşeni yaptı. `cashFlow.rows`
 * raporda TAM OLARAK `openLots`/`sales` ile aynı sebeple taşınıyor:
 * kullanıcı "bu temettü toplamı nereden çıktı" diye sorabilmeli. Dökümü
 * göstermeyen bir toplam denetlenemez, ve bu ekran denetlenebilirliğin son
 * halkası.
 */

import { UyariIkonu } from "../ui/UyariIkonu";
import { BilgiBasligi, FormulBasligi } from "../ui/BilgiIkonu";
import { TemettuHaric, temettuHaricMi } from "../components/TemettuHaric";
import { HesapAdi, hesapSeridi } from "../ui/HesapAdi";
import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import type {
  HesapRengi,
  Absence,
  Account,
  CashFlow,
  CashMovement,
  CashMovementBucket,
  DegerFormuluKodu,
  GainRatio,
  GunlukGetiri,
  PortfolioReport,
  PositionValuation,
  StampedAmount,
  ValuationNote,
  YillikKarsilik,
} from "@parafon/contracts/local-api";
import { MIKTAR_HANE, formatDecimal, formatMoney, formatRatio, decimalSign, ratioSign } from "../lib/decimal";
import { assetKey, assetLabel, formatIsoDate, todayIso, TRANSACTION_TYPE_LABEL } from "../lib/format";
import {
  BASIS_KIND_LABEL,
  VALUATION_NOTE_TEXT,
  anMetni,
  bayatlikMetni,
  beklentiMetni,
} from "../lib/valuationText";
import type { NoteTone } from "../lib/valuationText";
import {
  RAPOR_PARA_BIRIMLERI,
  kayitliRaporParaBirimi,
  raporParaBiriminiKaydet,
} from "../lib/raporParaBirimi";
import { kayitliRaporGorunumu, raporGorunumunuKaydet, type RaporGorunumu } from "../lib/raporGorunumu";
import { kayitliKartSeti } from "../lib/kartSeti";
import { nakitTakibiDurumu } from "../lib/nakitTakibi";
import { BUTUN_DEFTER, kapsamBellegi, useKapsamSecenekleri } from "../lib/hesapKapsami";
import { sanalVeriEsitle } from "../lib/sanalHesap";
import { useCentral } from "../central/CentralContext";
import { HesapSecici } from "../components/HesapSecici";
import { SanalNotlar } from "../components/SanalNotlar";
import { Aciklama } from "../ui/Aciklama";
import { M, YEREL } from "../dil";
import { VARSAYILAN_SIRALAMA, sirala, sonrakiSiralama, type SiraSutunu, type Siralama } from "../lib/varlikSiralama";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync } from "../repo/useAsync";
import { useSinirlar } from "../kabiliyet/KabiliyetSaglayici";
import { KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import {
  Button,
  DateInput,
  EmptyState,
  ErrorBlock,
  Field,
  LoadingBlock,
  Notice,
  NumberCell,
  Screen,
  Select,
  Tag,
  cx,
} from "../ui/primitives";
import { motorMetni, motorMetniVeya } from "../dil/motorMetni";

export function ReportScreen() {
  const { repo, revision } = useRepositoryContext();
  const [asOf, setAsOf] = useState("");
  // Gelir modeli: ücretsizde yalnızca bugün (geçmiş gün ayrı kabiliyet).
  const { gecmisGun } = useSinirlar();
  // Son seçim hatırlanır (kullanıcı kararı, 01.10.2026) — `raporParaBirimi.ts`.
  const [currency, setCurrencyState] = useState<string>(kayitliRaporParaBirimi);
  const setCurrency = useCallback((kod: string) => {
    setCurrencyState(kod);
    raporParaBiriminiKaydet(kod);
  }, []);
  const [acik, setAcik] = useState<string | null>(null);
  const merkez = useCentral();

  // HESAP KAPSAMI (back-test, 14.10.2026): bütün defter (gerçek hesaplar),
  // tek bir gerçek hesap ya da tek bir sanal hesap. Son seçim yalnızca bellekte.
  const [kapsam, setKapsamState] = useState<string>(kapsamBellegi.rapor);
  const setKapsam = useCallback((id: string) => {
    setKapsamState(id);
    kapsamBellegi.raporYaz(id);
  }, []);
  const kapsamVerisi = useKapsamSecenekleri(repo, revision);
  const sanal = kapsamVerisi.sanallar.find((h) => h.id === kapsam) ?? null;
  const kapsamGecerli =
    kapsam === BUTUN_DEFTER || kapsamVerisi.yukleniyor || kapsamVerisi.secenekler.some((s) => s.id === kapsam)
      ? kapsam
      : BUTUN_DEFTER;

  // Nakit takibi kasa düzeyinde kapatılabilir (kullanıcı kararı 14.10.2026);
  // kapalıyken motor nakdi hiç hesaplamıyor, ekran da nakit kutusunu göstermiyor.
  // Sanal hesapta nakit takibi yok (motor kapalı değerler).
  const load = useCallback(async () => {
    if (sanal !== null) {
      await sanalVeriEsitle(repo, merkez, sanal, currency);
      const report = await repo.valuation({
        ...(asOf === "" ? {} : { asOf }),
        reportCurrency: currency,
        accountIds: [sanal.id],
      });
      return { report, nakitAcik: false };
    }
    const nakit = await nakitTakibiDurumu(repo);
    const report = await repo.valuation({
      ...(asOf === "" ? {} : { asOf }),
      reportCurrency: currency,
      nakitTakibi: nakit.acik,
      ...(nakit.secim === "kapali" ? { nakitKapaliSecildi: true } : {}),
      ...(kapsamGecerli === BUTUN_DEFTER ? {} : { accountIds: [kapsamGecerli] }),
    });
    return { report, nakitAcik: nakit.acik };
    // `merkez` her çizimde yeni nesne olabilir; eşitleme kendi belleğiyle tekrarlanmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, asOf, currency, kapsamGecerli, sanal]);
  const { state, reload } = useAsync<{ readonly report: PortfolioReport; readonly nakitAcik: boolean }>(load, [
    repo,
    asOf,
    currency,
    revision,
    kapsamGecerli,
    sanal,
  ]);
  /**
   * HESAP ADLARI AYRI YÜKLENİYOR ve bu bilinçli: motor hesabı `accountId` ile
   * anıyor çünkü ad bir SUNUM bilgisi ve rapora girerse aynı olgu iki yerde
   * tutulur (hesap yeniden adlandırıldığında eski rapor yalan söylerdi).
   * Ad burada, gösterim anında bağlanıyor.
   */
  const hesaplar = useAsync<readonly Account[]>(
    useCallback(() => repo.accounts.list(true), [repo]),
    [repo, revision],
  );
  const hesapAdi = useCallback<HesapBul>(
    (id: string) => {
      const a = hesaplar.state.data?.find((x) => x.id === id);
      if (a === undefined) {
        const s = kapsamVerisi.sanallar.find((x) => x.id === id);
        if (s !== undefined) return { ad: s.ad, renk: s.renk };
      }
      return { ad: a?.name ?? id, renk: a?.renk };
    },
    [hesaplar.state.data, kapsamVerisi.sanallar],
  );

  return (
    <Screen
      title={M.rapor.ekran.baslik}
      description={M.rapor.ekran.aciklama}
      toolbar={
        <div className="flex flex-wrap items-end gap-2">
          <HesapSecici id="rapor-hesap" secenekler={kapsamVerisi.secenekler} deger={kapsamGecerli} onChange={setKapsam} />
          {gecmisGun ? (
            <Field label={M.rapor.ekran.tarihItibariyla} htmlFor="rapor-asof" className="w-40">
              <DateInput
                id="rapor-asof"
                value={asOf}
                max={todayIso()}
                onChange={(e) => setAsOf(e.currentTarget.value)}
              />
            </Field>
          ) : (
            <div className="flex w-40 flex-col gap-1">
              <span className="text-[12px] font-semibold text-muted">{M.rapor.ekran.tarihItibariyla}</span>
              <KilitliKabiliyet ne={M.rapor.ekran.gecmisGun} kisa={M.rapor.ekran.bugun} className="text-[13px]" />
            </div>
          )}
          <Field label={M.rapor.ekran.raporParaBirimi} htmlFor="rapor-para" className="w-52">
            <Select
              id="rapor-para"
              value={currency}
              options={[...RAPOR_PARA_BIRIMLERI]}
              onChange={(e) => setCurrency(e.currentTarget.value)}
            />
          </Field>
          {asOf !== "" && <Button onClick={() => setAsOf("")}>{M.rapor.ekran.buguneDon}</Button>}
        </div>
      }
    >
      {state.status === "loading" && state.data === undefined && (
        <LoadingBlock label={M.rapor.ekran.degerleniyor} />
      )}
      {state.status === "error" && <ErrorBlock error={state.error} onRetry={reload} />}

      {sanal !== null && <SanalNotlar tanim={sanal} asOf={asOf === "" ? undefined : asOf} />}
      {state.data !== undefined && (
        <RaporGovde
          report={state.data.report}
          nakitAcik={state.data.nakitAcik}
          hesapAdi={hesapAdi}
          acik={acik}
          onToggle={(key) => setAcik((prev) => (prev === key ? null : key))}
          temettuHaric={temettuHaricMi(sanal)}
        />
      )}
    </Screen>
  );
}

/** Hesabın adı ve rengi (silinmiş hesap dahil; bilinmeyen kimlik adı olarak). */
type HesapBul = (id: string) => { readonly ad: string; readonly renk: HesapRengi | undefined };

// ---------------------------------------------------------------------------

function RaporGovde({
  report,
  nakitAcik,
  hesapAdi,
  acik,
  onToggle,
  temettuHaric = false,
}: {
  readonly temettuHaric?: boolean;
  readonly report: PortfolioReport;
  readonly nakitAcik: boolean;
  readonly hesapAdi: HesapBul;
  readonly acik: string | null;
  readonly onToggle: (key: string) => void;
}) {
  // NAKİT DE BİR CEVAPTIR: yalnızca yatırma/çekme yazılmış bir defterde
  // pozisyon yoktur ama söylenecek bir şey VARDIR. Bu koşula `cashMovement`
  // eklenmeseydi o defter "değerlenecek bir şey yok" görürdü.
  if (
    report.positions.length === 0 &&
    report.excluded.length === 0 &&
    report.cashMovement === null
  ) {
    return (
      <EmptyState
        title={M.rapor.bos.baslik}
        description={M.rapor.bos.aciklama}
      />
    );
  }

  return (
    <div className="space-y-4 p-4">
      <p className="text-[12px] text-muted">
        {M.rapor.ust.onEk}
        <span className="pf-num font-semibold text-ink">{formatIsoDate(report.date)}</span>
        {M.rapor.ust.ara}
        <span className="pf-num font-semibold text-ink">{report.reportCurrency}</span>
        {M.rapor.ust.sonEk}
        {!nakitAcik && M.rapor.ust.nakitKapali}
      </p>

      {/* KURAL 3 — eksik toplam toplamların ÜSTÜNDE duyurulur. */}
      {!report.complete && (
        <Notice tone="warn" title={M.rapor.eksik.baslik}>
          <p>{M.rapor.eksik.metin}</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {report.excluded.map((e) => (
              <li key={assetKey(e.asset)}>
                <span className="font-medium text-ink">{assetLabel(e.asset)}</span> — {motorMetni(e.reason)}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {kayitliKartSeti() === "temel" ? (
        <TemelKartlar report={report} temettuHaric={temettuHaric} />
      ) : (
        <Toplamlar report={report} nakitAcik={nakitAcik} temettuHaric={temettuHaric} />
      )}

      <NakitBolumu cash={report.cashMovement} report={report} hesapAdi={hesapAdi} />

      {/* Kutular içerik kadar geniş (kullanıcı, 14.10.2026): tüm satıra
          yayılan kutu, kısa içeriğin yanında boş alan bırakıyordu. */}
      {report.notes.length > 0 && (
        <div className="w-fit max-w-full">
          <DamgaListesi notes={report.notes} title={M.rapor.damga.rapor} yalnizcaAnormallik />
        </div>
      )}

      <PozisyonTablosu report={report} acik={acik} onToggle={onToggle} temettuHaric={temettuHaric} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Temel kart seti (gelen karar 02.11.2026)
// ---------------------------------------------------------------------------

/**
 * TEMEL KART SETİ — "ne koydum, ne oldu, yılda kaç eder" sırasıyla:
 * Günlük getiri, Toplam maliyet, Toplam varlık, Getiri, Mevduat karşılığı.
 * Varsayılan; önceki kartlar (`Toplamlar`) Ayarlar'dan seçilir.
 *
 * Sayıların hepsi MOTORUN: toplam maliyet = asıl getirinin net yatırılanı,
 * toplam varlık = nakit dahil değer, getiri = asıl getiri, mevduat karşılığı
 * = para ağırlıklı yıllık (getiri % × 365 ÷ ağırlıklı ortalama gün) ve
 * altında o gün. Burada hesap yok. Bütün kartlar ücretsiz kasada da açık
 * (temel bilgi); asıl getirinin kilidi yalnızca ayrıntılı sette.
 *
 * Nakit takibi kapalıysa asıl getiri yok (sebebi motorda); toplam varlık
 * o zaman varlıkların değeridir — ⓘ formülü bunu söyler.
 */
function TemelKartlar({
  report,
  temettuHaric = false,
}: {
  readonly report: PortfolioReport;
  readonly temettuHaric?: boolean;
}) {
  const asil = report.asilGetiri;
  const g = report.gunluk;
  const alt = temettuHaric ? <TemettuHaric /> : undefined;
  const varlik = asil.deger ?? report.totalValue;
  const varlikYok = asil.deger === null ? report.absent.totalValue : undefined;
  return (
    <section aria-label={M.rapor.kart.bolum} className="grid gap-3 sm:flex sm:flex-wrap sm:items-stretch sm:[&>*]:min-w-[10rem]">
      <OranKart
        title={M.rapor.kart.gunlukGetiri}
        formulKodu="gunlukGetiri"
        value={g?.tutar ?? null}
        yokluk={report.gunlukYok}
        ratio={g?.oran ?? null}
        oranYoklugu={g?.oranYok}
        paydaAdi={M.rapor.terim.oncekiGunDegeri}
        {...(g === undefined ? {} : { formul: gunlukFormulu(g) })}
        {...(g?.sonHareketGunu === true
          ? { footer: <p className="mt-1 text-[11px] text-faint">{M.rapor.kart.sonIslemGunu(formatIsoDate(g.gun))}</p> }
          : {})}
      />
      <Kart title={M.rapor.kart.toplamMaliyet} value={asil.netYatirilan} yokluk={asil.absent.getiri} formulKodu="toplamMaliyet" />
      <Kart title={M.rapor.kart.toplamVarlik} value={varlik} yokluk={varlikYok ?? asil.absent.getiri} formulKodu="toplamVarlik" />
      <OranKart
        title={M.rapor.kart.getiri}
        formulKodu="getiri"
        alt={alt}
        value={asil.getiri}
        yokluk={asil.absent.getiri}
        ratio={asil.oran}
        oranYoklugu={asil.absent.oran}
        paydaAdi={M.rapor.terim.toplamMaliyet}
        formul={
          <FormulSatiri
            terimler={[
              { tutar: asil.deger, ad: M.rapor.terim.toplamVarlik },
              { tutar: asil.netYatirilan, ad: M.rapor.terim.toplamMaliyet, eksi: true },
            ]}
            sonuc={asil.getiri}
          />
        }
      />
      <div className="pf-card p-3" data-kart-bolum="ozet">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
          <FormulBasligi kod="mevduatKarsiligi">{M.rapor.kart.mevduatKarsiligi}</FormulBasligi>
        </p>
        {alt}
        <div className="pf-num mt-1 text-[20px] font-semibold">
          {asil.yillik === null ? (
            <Yokluk yokluk={asil.absent.yillik} />
          ) : (
            <span className="pf-tek text-ink">{formatRatio(asil.yillik)}</span>
          )}
        </div>
        {asil.yasGun !== null && (
          // Yalnızca VERİ: ağırlıklı ortalama gün.
          <p className="mt-1 text-[11px] text-muted">
            {M.rapor.kart.agirlikliOrtalama}
            <span className="pf-num text-ink">{asil.yasGun.toLocaleString(YEREL)}</span>
            {M.rapor.kart.gunSonEk(asil.yasGun)}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * ORAN ÖNDE KART: büyük yazıyla yüzde, altında küçük yazıyla tutar (gelen
 * karar 02.11.2026). Balon yüzdenin üstünde: formül ve oranın paydası. Oran
 * yoksa (ör. önceki gün değer yok, net yatırılan sıfır ya da eksi) büyük
 * satırda sebepli "—", tutar yine altta ve balonu onda.
 */
function OranKart({
  title,
  formulKodu,
  value,
  yokluk,
  ratio,
  oranYoklugu,
  formul,
  paydaAdi,
  alt,
  footer,
}: {
  readonly title: string;
  readonly formulKodu: DegerFormuluKodu;
  readonly value: StampedAmount | null;
  readonly yokluk: Absence | undefined;
  readonly ratio: GainRatio | null;
  readonly oranYoklugu: Absence | undefined;
  readonly formul?: ReactNode;
  readonly paydaAdi?: string;
  readonly alt?: ReactNode;
  readonly footer?: ReactNode;
}) {
  const tutar = (v: StampedAmount) => (
    <NumberCell {...(bayatMi(v) ? {} : { sign: decimalSign(v.amount) })} currency={v.currency}>
      {formatMoney(v.amount, { signed: true })}
    </NumberCell>
  );
  return (
    <div className="pf-card p-3" data-kart-bolum="ozet">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
        <FormulBasligi kod={formulKodu}>{title}</FormulBasligi>
      </p>
      {alt}
      {value === null ? (
        <div className="pf-num mt-1 text-[20px] font-semibold">
          <Yokluk yokluk={yokluk} />
        </div>
      ) : ratio === null ? (
        <>
          <div className="pf-num mt-1 text-[20px] font-semibold">
            <Yokluk yokluk={oranYoklugu} />
          </div>
          <div className="mt-0.5 text-[12px]">
            <TutarTetik value={value} icerik={tutarBalonu(value, formul, undefined, paydaAdi)}>
              {tutar(value)}
            </TutarTetik>
          </div>
        </>
      ) : (
        <>
          <div className="mt-1 text-[20px] font-semibold">
            <TutarTetik value={value} icerik={tutarBalonu(value, formul, ratio, paydaAdi)}>
              <span className={cx("pf-num pf-tek", !bayatMi(value) && ratioSign(ratio.ratio))}>{formatRatio(ratio.ratio)}</span>
            </TutarTetik>
          </div>
          <div className="mt-0.5 text-[12px]">{tutar(value)}</div>
        </>
      )}
      {footer}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toplamlar
// ---------------------------------------------------------------------------

function Toplamlar({
  report,
  nakitAcik,
  temettuHaric = false,
}: {
  readonly report: PortfolioReport;
  readonly nakitAcik: boolean;
  /** Sanal hesap kuralı temettüyü yok sayıyor (19.10.2026): getiri başlıklarının altında etiket. */
  readonly temettuHaric?: boolean;
}) {
  const cf = report.totalCashFlow;
  const asil = report.asilGetiri;
  // Gelir modeli: Asıl getiri geçiş paketinde; kilitliyse kutu kilit ifadesiyle.
  const { asilGetiri: asilAcik } = useSinirlar();
  const alt = temettuHaric ? <TemettuHaric /> : undefined;
  // Yokluğun sebebi MOTORDAN. Eskiden her kartın sabit bir "ya o ya bu"
  // cümlesi vardı; hangisinin doğru olduğunu bilen motordu.
  const yok = report.absent;
  return (
    <section aria-label={M.rapor.kart.bolum} className="grid gap-3 sm:flex sm:flex-wrap sm:items-stretch sm:[&>*]:min-w-[10rem]">
      <Kart title={M.rapor.kart.piyasaDegeri} value={report.totalValue} yokluk={yok.totalValue} formulKodu="piyasaDegeri" />
      <Kart
        title={M.rapor.kart.gunlukGetiri}
        value={report.gunluk?.tutar ?? null}
        signed
        ratio={report.gunluk?.oran ?? null}
        yokluk={report.gunlukYok}
        formulKodu="gunlukGetiri"
        paydaAdi={M.rapor.terim.oncekiGunDegeri}
        {...(report.gunluk === undefined ? {} : { formul: gunlukFormulu(report.gunluk) })}
        {...(report.gunluk?.sonHareketGunu === true
          ? { footer: <p className="mt-1 text-[11px] text-faint">{M.rapor.kart.sonIslemGunu(formatIsoDate(report.gunluk.gun))}</p> }
          : {})}
      />
      <Kart title={M.rapor.kart.maliyet} value={report.totalCost} yokluk={yok.totalCost} formulKodu="maliyet" />
      <Kart
        title={M.rapor.kart.gerceklesmemisKazanc}
        alt={alt}
        value={report.totalUnrealizedGain}
        signed
        ratio={report.totalUnrealizedGainRatio}
        yokluk={yok.totalUnrealizedGain}
        formulKodu="mevcutGetiri"
        temettuHaric={temettuHaric}
        paydaAdi={CEPTEN}
        formul={
          <FormulSatiri
            terimler={[
              { tutar: report.totalValue, ad: M.rapor.terim.deger },
              { tutar: report.totalCost, ad: M.rapor.terim.maliyet, eksi: true },
            ]}
            sonuc={report.totalUnrealizedGain}
          />
        }
      />
      <Kart
        title={M.rapor.kart.gerceklesmisKazanc}
        alt={alt}
        value={report.totalRealizedGain}
        signed
        ratio={report.totalRealizedGainRatio}
        yokluk={yok.totalRealizedGain}
        formulKodu="gerceklesmisKazanc"
        paydaAdi={SATILAN}
      />
      <Kart
        title={M.rapor.kart.nakitAkisiNet}
        alt={alt}
        value={cf?.net ?? null}
        signed
        yokluk={yok.totalCashFlowNet}
        formulKodu="nakitAkisi"
        temettuHaric={temettuHaric}
        formul={<FormulSatiri terimler={nakitTerimleri(cf, temettuHaric)} sonuc={cf?.net ?? null} />}
        footer={
          cf === null ? undefined : (
            // Yalnızca VERİ kutuda kalıyor: gelir ve gider birer sayı, açıklama
            // değil. Kullanıcı bunları görmek için balon açmak zorunda kalmamalı.
            <p className="mt-1 text-[11px] text-muted">
              {M.rapor.kart.gelir}
              <SatirIci value={cf.income} yokluk={cf.absent.income} />
              {M.rapor.kart.gider}
              <SatirIci value={cf.expense} yokluk={cf.absent.expense} />
            </p>
          )
        }
      />
      <Kart
        title={M.rapor.kart.gerceklesmisGetiri}
        alt={alt}
        value={report.totalRealizedReturn}
        signed
        ratio={report.totalRealizedReturnRatio}
        yokluk={yok.totalRealizedReturn}
        formulKodu="gerceklesmisGetiri"
        temettuHaric={temettuHaric}
        paydaAdi={SATILAN}
        formul={
          <FormulSatiri
            terimler={[{ tutar: report.totalRealizedGain, ad: M.rapor.terim.satisKazanci }, ...nakitTerimleri(cf, temettuHaric)]}
            sonuc={report.totalRealizedReturn}
          />
        }
      />
      {!asilAcik ? (
        <div className="pf-card p-3" aria-label={M.rapor.kart.asilGetiri} data-kart-bolum="ozet">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">{M.rapor.kart.asilGetiri}</p>
          <KilitliKabiliyet ne={M.rapor.kart.asilGetiri} className="mt-2" />
        </div>
      ) : (
      <Kart
        title={M.rapor.kart.asilGetiri}
        alt={alt}
        value={asil.getiri}
        signed
        ratio={asil.oran}
        yokluk={asil.absent.getiri}
        formulKodu="asilGetiri"
        paydaAdi={M.rapor.terim.yatirilanCekilen}
        formul={
          <FormulSatiri
            terimler={[
              { tutar: asil.deger, ad: M.rapor.terim.degerNakitDahil },
              { tutar: asil.netYatirilan, ad: M.rapor.terim.yatirilanCekilen, eksi: true },
            ]}
            sonuc={asil.getiri}
          />
        }
        footer={
          asil.getiri === null ? undefined : (
            // Yalnızca VERİ: para ağırlıklı yıllık ve yaş.
            <p className="mt-1 text-[11px] text-muted">
              {M.rapor.kart.yillik}
              {asil.yillik === null ? (
                <Yokluk yokluk={asil.absent.yillik} />
              ) : (
                <span className="pf-num text-ink">{formatRatio(asil.yillik)}</span>
              )}
              {asil.yasGun !== null && (
                <>
                  {" "}
                  · <span className="pf-num">{asil.yasGun.toLocaleString(YEREL)}</span>
                  {M.rapor.kart.gunSonEk(asil.yasGun)}
                </>
              )}
            </p>
          )
        }
      />
      )}
      {nakitAcik && (
        <Kart
          title={M.rapor.kart.netNakitHareketi}
          value={report.cashMovement?.total ?? null}
          signed
          kazancRengi={false}
          yokluk={yok.cashMovementTotal}
          aciklama={<>{M.rapor.kart.netNakitAciklama}</>}
          footer={
            report.cashMovement === null ? undefined : (
              // Yalnızca VERİ: kaç kova var, kaçı toplama girdi.
              <p className="mt-1 text-[11px] text-muted">
                <span className="pf-num text-ink">{report.cashMovement.buckets.length}</span>
                {M.rapor.kart.kovaSonEk(report.cashMovement.buckets.length)}
              </p>
            )
          }
        />
      )}
      <div className="pf-card p-3" data-kart-bolum="ozet">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
          <FormulBasligi kod="yillikKarsilik">{M.rapor.kart.yillikKarsilik}</FormulBasligi>
        </p>
        {alt}
        <div className="mt-1 text-[20px] font-semibold">
          <YillikDeger
            k={report.yillikKarsilik}
            kapsam="toplam"
            buyuk
            geriYatirim={report.totalGeriYatirim === null ? null : { tutar: report.totalGeriYatirim }}
          />
        </div>
      </div>
      <Kart
        title={M.rapor.kart.kesilenVergi}
        value={report.totalWithheldTax}
        yokluk={yok.totalWithheldTax}
        formulKodu="kesilenVergi"
      />
    </section>
  );
}

/**
 * ÖZET KARTI.
 *
 * YOKLUĞUN SEBEBİ DE BALONDA. Eskiden `missing` metni kartın içinde, sayının
 * altında duruyordu; yedi kartın birkaçı boşsa üst bölüm bir gerekçe duvarına
 * dönüyordu. Sebep silinmedi — "—" işaretinin KENDİSİ balonun tetikleyicisi
 * oldu. J'nin 1. kuralı ("hiçbir `null` sebebi söylenmeden gösterilmez")
 * duruyor, yalnızca ikinci katmana indi; tutarların dayanağına yapılanın aynısı.
 *
 * "—" YALNIZCA ÖLÇÜLEMEDİYSE İŞARETLİ (N şeridi). L3'te "her zaman
 * işaretli" idi ve gerekçesi "eksik toplam her zaman bir anormalliktir"di —
 * yanlış bir öncül: satışı olmayan bir defterde gerçekleşmiş kazancın
 * yokluğu bir eksiklik değil, defterin normal hâli. Canlıdaki üç fonluk
 * defterde bu, kartların üçünü birden uyarıya boyuyordu. Ayrım motordan
 * geliyor (`Absence`); ekran yalnızca uyguluyor.
 *
 * `aciklama` kartın kalıcı açıklaması (nasıl hesaplandığı); o da balonda.
 * `footer` yalnızca VERİ içindir — gelir/gider dökümü gibi. Açıklama metni
 * footer'a yazılırsa kutu yine dolar.
 */
/** Ana sayfa da aynı kartı kullanır: dayanak, bayatlık ve yokluk sebebi tek kopya. */
export function Kart({
  title,
  value,
  yokluk,
  signed = false,
  kazancRengi = true,
  ratio,
  aciklama,
  footer,
  alt,
  formulKodu,
  temettuHaric = false,
  formul,
  paydaAdi,
}: {
  readonly title: string;
  /** Başlığın altında küçük etiket (ör. "temettü hariç"). */
  readonly alt?: ReactNode;
  readonly value: StampedAmount | null;
  /** `value` null ise niçin — MOTORUN söylediği (`PortfolioReport.absent`). */
  readonly yokluk: Absence | undefined;
  readonly signed?: boolean;
  /**
   * YEŞİL/KIRMIZI BİR YARGIDIR, BİÇİM DEĞİL.
   *
   * `signed` yalnızca "+/− göster" demek; rengi de ona bağlamak bir kusur
   * üretti: net nakit hareketinde eksi bir sayı ZARAR DEĞİL, açılış
   * tutarının deftere yazılmadığını söyleyen beklenen bir sonuçtur. Kırmızı
   * boyamak, "bakiye değil net hareket" ayrımının kurtarmaya çalıştığı
   * okumayı ekranda geri getiriyordu.
   *
   * Kazanç/zarar kartlarında renk anlamlı ve orada duruyor; anlamlı
   * olmadığı yerde kapatılıyor.
   */
  readonly kazancRengi?: boolean;
  /** Oran gösterilecek kartlarda verilir; verilmezse oran satırı hiç çıkmaz. */
  readonly ratio?: GainRatio | null;
  /** Kartın kalıcı açıklaması — balonda gösterilir, kutuda DEĞİL. */
  readonly aciklama?: ReactNode;
  /** Yalnızca VERİ. Açıklama için `aciklama` kullanılır. */
  readonly footer?: ReactNode;
  /** Başlığın yanında ⓘ: formül sözlükten (19.10.2026). */
  readonly formulKodu?: DegerFormuluKodu;
  readonly temettuHaric?: boolean;
  /** Formülün bu değerle yazılmış hâli (balonun ilk satırı). */
  readonly formul?: ReactNode;
  /** Oranın paydasının adı (ör. "cepten çıkan para"). */
  readonly paydaAdi?: string;
}) {
  return (
    <div className="pf-card p-3" data-kart-bolum="ozet">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
        {formulKodu !== undefined ? (
          <FormulBasligi kod={formulKodu} temettuHaric={temettuHaric}>
            {title}
          </FormulBasligi>
        ) : aciklama !== undefined ? (
          <BilgiBasligi ad={title} icerik={aciklama}>
            {title}
          </BilgiBasligi>
        ) : (
          title
        )}
      </p>
      {alt}
      {value === null ? (
        <div className="pf-num mt-1 text-[20px] font-semibold">
          <Yokluk yokluk={yokluk} />
        </div>
      ) : (
        <>
          <div className="mt-1 text-[20px] font-semibold">
            <TutarTetik value={value} icerik={tutarBalonu(value, formul, ratio, paydaAdi)}>
              <NumberCell
                {...(signed && kazancRengi && !bayatMi(value)
                  ? { sign: decimalSign(value.amount) }
                  : {})}
                currency={value.currency}
              >
                {formatMoney(value.amount, { signed })}
              </NumberCell>
              {ratio !== undefined && <OranMetni value={ratio} />}
            </TutarTetik>
          </div>
        </>
      )}
      {footer}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Net nakit hareketi (M şeridi)
// ---------------------------------------------------------------------------

/**
 * NAKİT BÖLÜMÜ.
 *
 * Başlıktaki kelime "bakiye" DEĞİL ve bu ekranın en önemli kararı. Parafon
 * defteri kullanıcının yazdığı satırlardan ibaret; açılış tutarı yazılmamışsa
 * türetilen sayı negatiftir. "Bakiye" demek o defterde YANLIŞ bir cümle
 * kurmak olurdu; "net hareket" ise eksik defterde de doğru kalıyor.
 *
 * Bu yüzden NEGATİF SAYI UYARI RENGİNDE DEĞİL: §"ekranda görünen her işaret
 * bir anormallik demektir". Eksi bir net, eksik defterin beklenen sonucu.
 */
function NakitBolumu({
  cash,
  report,
  hesapAdi,
}: {
  readonly cash: CashMovement | null;
  readonly report: PortfolioReport;
  readonly hesapAdi: HesapBul;
}) {
  if (cash === null) return null;
  return (
    <section aria-label={M.rapor.nakit.bolum} className="space-y-3">
      {/* KURAL 3 — eksik toplam, toplamın ÜSTÜNDE duyurulur. */}
      {!cash.complete && (
        <Notice tone="warn" title={M.rapor.nakit.eksikBaslik}>
          <p>{M.rapor.nakit.eksikMetin}</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {cash.excluded.map((e) => (
              <li key={`${e.accountId}|${e.currency}`}>
                <HesapAdi renk={hesapAdi(e.accountId).renk} className="font-medium">
                  {hesapAdi(e.accountId).ad}
                </HesapAdi>{" "}
                <span className="pf-fig text-muted">{e.currency}</span> — {motorMetni(e.reason)}
              </li>
            ))}
          </ul>
        </Notice>
      )}
      <NakitTablosu cash={cash} report={report} hesapAdi={hesapAdi} />
    </section>
  );
}

/** Kova başına tür kırılımı — "bu sayı nereden çıktı"nın cevabı, balonda. */
function kirilimIcerigi(b: CashMovementBucket): ReactNode {
  return (
    <>
      {b.byType.map((k) => (
        <span key={k.type} className="pf-aciklama-satir">
          <span className="text-ink">{TRANSACTION_TYPE_LABEL[k.type]}</span>{" "}
          <span className="text-faint">({k.count})</span> ·{" "}
          <span className="pf-fig">
            {formatMoney(k.amount, { signed: true })} {b.currency}
          </span>
        </span>
      ))}
      {b.net !== null &&
        b.net.basis.map((d, i) => (
          <span key={`${d.ref}|${i}`} className="pf-aciklama-satir text-faint">
            {M.rapor.nakit.kasaSonHareket(d.ref, formatIsoDate(d.asOf))}
          </span>
        ))}
    </>
  );
}

function NakitTablosu({
  cash,
  report,
  hesapAdi,
}: {
  readonly cash: CashMovement;
  readonly report: PortfolioReport;
  readonly hesapAdi: HesapBul;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="pf-table pf-card w-auto overflow-hidden" data-kart-bolum="liste">
        <caption className="sr-only">{M.rapor.nakit.tabloBasligi}</caption>
        <thead>
          <tr>
            <th scope="col">{M.rapor.nakit.hesap}</th>
            {/* Para birimi KODU bir rakam değil: `pf-fig`, sağa yaslanmaz. */}
            <th scope="col" className="pf-fig">
              {M.rapor.nakit.paraBirimi}
            </th>
            <th scope="col" className="pf-num">
              {M.rapor.nakit.netHareket}
            </th>
            <th scope="col" className="pf-num">
              {M.rapor.nakit.karsiligi(report.reportCurrency)}
            </th>
          </tr>
        </thead>
        <tbody>
          {cash.buckets.map((b) => (
            <tr key={`${b.accountId}|${b.currency}`}>
              <td style={hesapSeridi(hesapAdi(b.accountId).renk)}>
                <HesapAdi renk={hesapAdi(b.accountId).renk}>{hesapAdi(b.accountId).ad}</HesapAdi>
              </td>
              <td className="pf-fig">{b.currency}</td>
              <td className="pf-num">
                {b.net === null ? (
                  <Aciklama
                    className="pf-aciklama-isaretli text-warn"
                    label={M.rapor.balon.neden}
                    icerik={
                      <>
                        <span className="pf-aciklama-satir">
                          {M.rapor.nakit.okunamayanSatir(b.unreadableIds.length)}
                        </span>
                        {b.unreadableIds.map((id) => (
                          <span key={id} className="pf-aciklama-satir pf-fig text-faint">
                            {id}
                          </span>
                        ))}
                        {kirilimIcerigi(b)}
                      </>
                    }
                  >
                    —
                  </Aciklama>
                ) : (
                  <Aciklama label={M.rapor.nakit.turKirilimi} icerik={kirilimIcerigi(b)}>
                    <NumberCell currency={b.currency}>
                      {formatMoney(b.net.amount, { signed: true })}
                    </NumberCell>
                  </Aciklama>
                )}
              </td>
              <td className="pf-num">
                {b.netConverted === null ? (
                  <Aciklama
                    className="pf-aciklama-isaretli text-warn"
                    label={M.rapor.balon.neden}
                    icerik={
                      <span className="pf-aciklama-satir">
                        {b.net === null
                          ? M.rapor.nakit.netYok
                          : M.rapor.nakit.kurYok(b.currency, report.reportCurrency, formatIsoDate(report.date))}
                      </span>
                    }
                  >
                    —
                  </Aciklama>
                ) : (
                  <TutarTetik value={b.netConverted} icerik={sorunSatiri(b.netConverted)}>
                    <NumberCell currency={b.netConverted.currency}>
                      {formatMoney(b.netConverted.amount, { signed: true })}
                    </NumberCell>
                  </TutarTetik>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Kart içi küçük tutar — gelir/gider gibi yardımcı bileşenler için.
 *
 * `yokluk` ZORUNLU: eskiden `null` düz bir "—" idi ve masrafı olmayan
 * varlığın gideriyle kuru bulunamayan temettü AYNI görünüyordu. Artık "—"
 * de `Yokluk` — sebep balonda, işaret yalnızca ölçülemediyse.
 */
function SatirIci({
  value,
  yokluk,
}: {
  readonly value: StampedAmount | null;
  readonly yokluk: Absence | undefined;
}) {
  if (value === null) return <Yokluk yokluk={yokluk} />;
  return (
    <span className="pf-num text-ink">
      {formatMoney(value.amount)} {value.currency}
    </span>
  );
}

// ---------------------------------------------------------------------------
// KURAL 2 — dayanak, artık AYRI BİR SATIR DEĞİL
// ---------------------------------------------------------------------------

/**
 * TUTARIN DAYANAĞI TUTARIN KENDİSİNDE.
 *
 * Önceki hâlinde her tutarın ALTINDA bir damga satırı vardı ("10.09.2026
 * ölçümüyle · taze (1 dayanak)"). Dokuz sütunlu bir tabloda bu, her hücreyi
 * iki satıra çıkarıp ekranı okunmaz hâle getiriyordu — üstelik satırların
 * çoğu "taze" diyordu, yani BAKILACAK BİR ŞEY OLMADIĞINI söylemek için yer
 * kaplıyordu.
 *
 * Yeni kural: **ekranda görünen her işaret bir anormallik demektir.**
 *
 * - Anormallik YOKSA tutar olduğu gibi görünür. Dayanak dökümü kaybolmaz,
 *   tutarın üzerine gelince açılır — J'nin "hiçbir tutar dayanağını
 *   söylemeden gösterilemez" kuralı duruyor, yalnızca ikinci katmana indi.
 * - Anormallik VARSA (bayat ölçüm) tutar uyarı rengine döner ve kesikli alt
 *   çizgi alır; sebebi balonun ilk satırındadır.
 *
 * `forAskedDate: false` bir anormallik DEĞİL: 2019 maliyetini çevirmek
 * 2019'un kurunu gerektirir ve o kur doğrudur. Bu yüzden tutarı işaretlemez.
 */
function bayatMi(value: StampedAmount): boolean {
  return bayatlikMetni(value) !== null;
}

/**
 * TUTAR BALONU (kullanıcı kararı 20.10.2026): YALNIZCA sorun ve formül.
 *
 *  - Sorun varsa ilk satır (bayat ölçüm; oranın paydası yoksa).
 *  - Formül en çok iki satır: tutarın eşitliği ve oranın bölmesi, bu
 *    değerlerle.
 *
 * Normal dayanak (fiyatın kaynağı, ölçüm günü, kur, partiler) balonda YOK:
 * satırın "Ayrıntı" panelinde (`DayanakOzeti`). Sorun da formül de yoksa
 * balon hiç yoktur — tutar düz yazılır. Anlamsız bilgi göstermemek, bilgiyi
 * silmek değil: dayanak bir tık uzakta, Ayrıntı'da.
 */
function sorunSatiri(value: StampedAmount): ReactNode {
  const b = bayatlikMetni(value);
  return b === null ? null : <span className="pf-aciklama-satir text-warn">{b}</span>;
}

/** Oranın bölmesi: "+650,00 ÷ 600,00 cepten çıkan para = +%108,33". */
function OranSatiri({
  pay,
  ratio,
  paydaAdi,
}: {
  readonly pay: StampedAmount;
  readonly ratio: GainRatio | null;
  readonly paydaAdi: string | undefined;
}) {
  if (ratio === null) {
    return <span className="pf-aciklama-satir text-warn">{M.rapor.balon.oranYok}</span>;
  }
  return (
    <span className="pf-aciklama-satir text-ink">
      <span className="pf-tek">{formatMoney(pay.amount, { signed: true })}</span> ÷{" "}
      <span className="pf-tek">{formatMoney(ratio.base)}</span> {paydaAdi ?? M.rapor.terim.payda} ={" "}
      <span className="pf-tek">{formatRatio(ratio.ratio)}</span>
    </span>
  );
}

/** Sorun + formül; ikisi de yoksa null (balon çizilmez). */
function tutarBalonu(
  value: StampedAmount,
  formul: ReactNode | undefined,
  ratio: GainRatio | null | undefined,
  paydaAdi: string | undefined,
): ReactNode | null {
  const sorun = sorunSatiri(value);
  const oran = ratio === undefined ? null : <OranSatiri pay={value} ratio={ratio} paydaAdi={paydaAdi} />;
  if (sorun === null && (formul === undefined || formul === null) && oran === null) return null;
  return (
    <>
      {sorun}
      {formul}
      {oran}
    </>
  );
}

/** Balonlu ya da düz tutar: içerik yoksa tetikleyici de yok. */
function TutarTetik({
  value,
  icerik,
  className,
  children,
}: {
  readonly value: StampedAmount;
  readonly icerik: ReactNode | null;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const bayat = bayatMi(value);
  if (icerik === null) return <span className={className}>{children}</span>;
  return (
    <Aciklama
      className={cx(className, bayat && "pf-aciklama-isaretli text-warn")}
      label={bayat ? M.rapor.balon.bayat : M.rapor.balon.nasil}
      icerik={icerik}
    >
      {children}
    </Aciklama>
  );
}

/**
 * AYRINTI PANELİNDEKİ DAYANAK: fiyatın ve kurun kaynağı, ölçüm günü —
 * balondan çıkan normal bilgi burada, düz metin. Alış partileri zaten
 * aşağıdaki tabloda; burada yalnızca değerin dayanakları.
 */
function DayanakOzeti({ p }: { readonly p: PositionValuation }) {
  const basis = (p.reportValue ?? p.marketValue)?.basis.filter((b) => b.kind !== "hareket") ?? [];
  if (basis.length === 0) return null;
  const tekil = basis.filter((b, i) => basis.findIndex((x) => x.kind === b.kind && x.ref === b.ref && x.asOf === b.asOf) === i);
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">{M.rapor.dayanak}</p>
      <ul className="mt-1 space-y-0.5 text-[12px] text-muted">
        {tekil.map((b, i) => (
          <li key={`${b.kind}|${b.ref}|${b.asOf}|${i}`}>
            <span className="text-ink">{BASIS_KIND_LABEL[b.kind]}</span>
            {b.kind === "kur" && <> {b.ref}</>}
            {b.source !== null && <> · {b.source}</>} ·{" "}
            <span className="pf-fig">{b.asOfTs === undefined ? formatIsoDate(b.asOf) : anMetni(b.asOfTs)}</span>
            {b.seans !== undefined && <> · {M.rapor.seans[b.seans]}</>}
            {b.forAskedDate && b.stale && <span className="text-warn"> · {beklentiMetni(b, b.staleDays)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formülün bu değerle yazılmış hâli (kullanıcı kararı 19.10.2026)
// ---------------------------------------------------------------------------

/**
 * DEĞER BALONUNUN İLK SATIRI: ⓘ'deki formül, bu sayının terimleriyle
 * ("1.260,00 değer − 1.050,00 maliyet = +210,00 TRY"). Terimler ve sonuç
 * RAPORDAN — motor verir, arayüz toplamaz; yalnızca yazar. Eksi bir terim
 * işlemin işaretini çevirerek yazılır ("+ −300" değil "− 300"). Sonuç ya da
 * hiçbir terim yoksa satır yok: genel formül ⓘ balonunda durur.
 */
interface Terim {
  readonly tutar: StampedAmount | null | undefined;
  readonly ad: string;
  /** Terim eksiyle mi katılıyor (ör. masraf, maliyet). */
  readonly eksi?: boolean;
}

function FormulSatiri({ terimler, sonuc }: { readonly terimler: readonly Terim[]; readonly sonuc: StampedAmount | null }) {
  const t = terimler.filter((x): x is Terim & { tutar: StampedAmount } => x.tutar != null);
  if (sonuc === null || t.length === 0) return null;
  return (
    <span className="pf-aciklama-satir text-ink">
      {t.map((x, i) => {
        const negatif = x.tutar.amount.startsWith("-");
        const govde = negatif ? x.tutar.amount.slice(1) : x.tutar.amount;
        const eksi = (x.eksi === true) !== negatif;
        const op = i === 0 ? (eksi ? "−" : "") : eksi ? "−" : "+";
        return (
          <span key={`${x.ad}|${i}`}>
            {i === 0 ? "" : " "}
            <span className="pf-tek">
              {op === "" ? "" : `${op} `}
              {formatMoney(govde)}
            </span>{" "}
            {x.ad}
          </span>
        );
      })}{" "}
      ={" "}
      <span className="pf-tek">
        {formatMoney(sonuc.amount, { signed: true })} {sonuc.currency}
      </span>
    </span>
  );
}

const CEPTEN = M.rapor.terim.cepten;
const SATILAN = M.rapor.terim.satilan;

function nakitTerimleri(cf: CashFlow | null, temettuHaric: boolean): readonly Terim[] {
  if (cf === null) return [];
  return [
    { tutar: cf.income, ad: temettuHaric ? M.rapor.terim.faiz : M.rapor.terim.temettuFaiz },
    { tutar: cf.expense, ad: M.rapor.terim.masraf, eksi: true },
  ];
}

/** Varlığın değeri: miktar × birim fiyat = fiyatın para biriminde → rapor para biriminde. */
function DegerFormulu({ p }: { readonly p: PositionValuation }) {
  if (p.unitPrice === null || p.marketValue === null || p.reportValue === null) return null;
  return (
    <span className="pf-aciklama-satir text-ink">
      <span className="pf-tek">{formatDecimal(p.quantity, { maxFractionDigits: MIKTAR_HANE })}</span>
      {M.rapor.terim.adetCarpi}
      <span className="pf-tek">
        {formatDecimal(p.unitPrice.amount, { maxFractionDigits: 8 })} {p.unitPrice.currency}
      </span>{" "}
      ={" "}
      <span className="pf-tek">
        {formatMoney(p.marketValue.amount)} {p.marketValue.currency}
      </span>
      {p.marketValue.currency !== p.reportValue.currency && (
        <>
          {" "}
          →{" "}
          <span className="pf-tek">
            {formatMoney(p.reportValue.amount)} {p.reportValue.currency}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * ORANIN YERİ: TUTARIN YANINDA, PARANTEZ İÇİNDE, DAHA KÜÇÜK.
 *
 * Alt satıra düşerse hücre iki satıra çıkıyor ve tablo yine şişiyor; tutarla
 * oranı ayrı satırlara koymak ikisini ayrı iki büyüklük gibi de gösteriyordu.
 * Parantez, oranın yanındaki sayıya AİT olduğunu söyleyen en kısa işaret.
 *
 * Kendi tetikleyicisi YOK ve olamaz: tutar zaten bir <button> ve düğme içine
 * düğme koymak geçersiz HTML'dir. Bu yüzden paydası, tutarın balonunda —
 * ikisi zaten tek bir soruyu yanıtlıyor: "bu sayı nereden çıktı".
 */
function OranMetni({ value }: { readonly value: GainRatio | null }) {
  if (value === null) return <span className="pf-tek ml-1 text-[0.85em] text-faint">({M.bicim.yuzde("—")})</span>;
  return (
    <span className={cx("pf-tek ml-1 text-[0.85em]", ratioSign(value.ratio))}>
      ({formatRatio(value.ratio)})
    </span>
  );
}

// ---------------------------------------------------------------------------
// Damga listesi
// ---------------------------------------------------------------------------

const TONE_CLASS: Readonly<Record<NoteTone, string>> = {
  danger: "border-danger-border bg-danger-soft text-danger",
  warn: "border-warn-border bg-warn-soft text-warn",
  info: "border-accent-border bg-accent-soft text-accent",
};

/**
 * DAMGA LİSTESİ.
 *
 * `yalnizcaAnormallik` ile çağrıldığında `info` tonlu damgalar GİZLENİR.
 * Sebebi bir kural: **damga bir anormalliğin adıdır.** "Kapalı pozisyon",
 * "Sermaye hareketi hariç" gibi damgalar hesabın NORMAL işleyişini anlatıyor;
 * bunları raporun tepesinde listelemek, bakılacak bir şey varmış izlenimi
 * veriyor ve gerçek anormallikleri kalabalığın içinde kaybediyordu.
 *
 * SİLİNMİYORLAR: pozisyonun "Ayrıntı" panelinde tonuna bakılmadan hepsi
 * duruyor. Kullanıcı "bu sayı niçin böyle" diye sorduğunda cevabın tamamı
 * orada — yalnızca soruyu sormadan önce ekranı doldurmuyorlar.
 */
function DamgaListesi({
  notes,
  title,
  yalnizcaAnormallik = false,
}: {
  readonly notes: readonly ValuationNote[];
  readonly title: string;
  readonly yalnizcaAnormallik?: boolean;
}) {
  const gosterilecek = yalnizcaAnormallik
    ? notes.filter((n) => VALUATION_NOTE_TEXT[n.code].tone !== "info")
    : notes;
  if (gosterilecek.length === 0) return null;
  return (
    <section aria-label={title} className="pf-card p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">{title}</p>
      <ul className="mt-2 space-y-1.5">
        {gosterilecek.map((n, i) => {
          const t = VALUATION_NOTE_TEXT[n.code];
          return (
            <li key={`${n.code}|${n.ref ?? ""}|${i}`} className="text-[10px]">
              {/* GEREKÇE EKRANDAN KALKTI, ERİŞİMDEN KALKMADI. Etiket balonun
                  tetikleyicisi: hangi damga olduğu görünür kalıyor, "niçin"
                  ve "ne yapmalı" bir tık/hover uzakta. */}
              <Aciklama
                className={cx(
                  "mr-1 inline-block rounded-pf border px-1.5 py-0.5 text-[10px]",
                  TONE_CLASS[t.tone],
                )}
                icerik={
                  <>
                    <span className="pf-aciklama-satir">{motorMetni(n.detail)}</span>
                    <span className="pf-aciklama-satir text-muted">{t.action}</span>
                  </>
                }
              >
                {t.label}
              </Aciklama>
              {n.ref !== undefined && <span className="pf-fig text-muted">{n.ref}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Damga ikonunun rengi: tonun kendisi (anormallik — renk burada yargı). */
const IKON_RENGI: Readonly<Record<NoteTone, string>> = {
  danger: "text-danger",
  warn: "text-warn",
  info: "text-accent",
};

/** Uyarı üçgeni (warn) ya da daire içinde ünlem (danger). */
function DamgaIkonu({ tone }: { readonly tone: NoteTone }) {
  return <UyariIkonu tone={tone === "danger" ? "danger" : "warn"} />;
}

/**
 * TABLO SATIRINDAKİ ROZETLER — YALNIZCA ANORMALLİKLER.
 *
 * `info` tonlu damgalar buraya çıkmıyor (gerekçe `DamgaListesi` başlığında).
 * Satırda bir rozet görünüyorsa bakılacak bir şey VAR demektir; görünmüyorsa
 * yok demektir. Rozetin tek anlamı bu olmalı, yoksa hiçbir anlamı kalmaz.
 */
function DamgaRozetleri({ notes }: { readonly notes: readonly ValuationNote[] }) {
  const teklesmis = [
    ...new Set(notes.filter((n) => VALUATION_NOTE_TEXT[n.code].tone !== "info").map((n) => n.code)),
  ];
  if (teklesmis.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {teklesmis.map((code) => {
        const t = VALUATION_NOTE_TEXT[code];
        return (
          // İKON (kullanıcı, 14.10.2026): satırda metin değil işaret; damganın
          // adı ve ne yapılacağı balonda. `title` KLAVYEYLE açılmadığı ve
          // dokunmatikte görünmediği için gerçek balon; ad ekran okuyucuya
          // `aria-label` ile.
          <Aciklama
            key={code}
            label={t.label}
            className={cx("inline-flex rounded-full p-0.5", IKON_RENGI[t.tone])}
            icerik={
              <>
                <span className="pf-aciklama-satir font-semibold">{t.label}</span>
                <span className="pf-aciklama-satir">{t.action}</span>
              </>
            }
          >
            <DamgaIkonu tone={t.tone} />
          </Aciklama>
        );
      })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pozisyon tablosu
// ---------------------------------------------------------------------------

/**
 * TAMAMI SATILMIŞ VARLIK: motor "kapalı pozisyon" dedi VE satışı var.
 *
 * Kullanıcı (10.10.2026): geçmişte alınıp bütünü satılmış varlıklar tabloyu
 * kalabalık ediyor; varsayılan gizli, istenince açılır. Miktar taşımayan
 * hareketlerden oluşan satır (yalnızca para yatırma, faiz…) satılmış bir
 * varlık DEĞİL — o satır görünür kalır. Kapalılık kararı motorun
 * (`kapali_pozisyon` damgası); burada yeniden hesaplanmıyor.
 *
 * Gizlemek yalnızca SATIRI saklar: toplamlar motordan geliyor ve gizli
 * satırların gerçekleşmiş kazancını, nakit akışını her durumda içeriyor.
 */
function tamamiSatilmis(p: PositionValuation): boolean {
  return p.sales.length > 0 && p.notes.some((n) => n.code === "kapali_pozisyon");
}

function PozisyonTablosu({
  report,
  acik,
  onToggle,
  temettuHaric = false,
}: {
  readonly report: PortfolioReport;
  readonly acik: string | null;
  readonly onToggle: (key: string) => void;
  readonly temettuHaric?: boolean;
}) {
  // Saklanmıyor: her açılışta gizli (kilitliyken dışarıda iz kalmasın).
  const [satilmislariGoster, setSatilmislariGoster] = useState(false);
  // Dar ekranda tablo mu kart mı (kullanıcı, 11.10.2026): varsayılan tablo.
  const [gorunum, setGorunumState] = useState<RaporGorunumu>(kayitliRaporGorunumu);
  const setGorunum = (g: RaporGorunumu) => {
    setGorunumState(g);
    raporGorunumunuKaydet(g);
  };
  // SIRALAMA (Gelen kararlar 28.10.2026): saklanmaz, her açılışta değere göre
  // büyükten küçüğe. Kart görünümünde başlık gizli; sıra yine geçerli.
  const [siralama, setSiralama] = useState<Siralama>(VARSAYILAN_SIRALAMA);
  if (report.positions.length === 0) return null;

  const satilmisSayisi = report.positions.filter(tamamiSatilmis).length;
  const satirlar = sirala(
    satilmislariGoster ? report.positions : report.positions.filter((p) => !tamamiSatilmis(p)),
    siralama,
    (p) => assetLabel(p.asset),
    YEREL,
  );
  const sira = (sutun: SiraSutunu) => ({
    "aria-sort": siralama.sutun !== sutun ? ("none" as const) : siralama.yon === "artan" ? ("ascending" as const) : ("descending" as const),
  });
  const ok = (sutun: SiraSutunu, ad: string) => (
    <SiraOku ad={ad} durum={siralama.sutun === sutun ? siralama.yon : null} onSirala={() => setSiralama((o) => sonrakiSiralama(o, sutun))} />
  );
  const deger = M.rapor.tablo.deger(report.reportCurrency);
  const maliyet = M.rapor.tablo.maliyet(report.reportCurrency);
  // Payda eksikliği bütün kapsamın bilgisi: değeri ölçülemeyen varlık varsa
  // paylar ölçülebilenlerin toplamına göre — başlıkta TEK işaret.
  const paydaEksik = report.positions.some((p) => p.pay !== null && !p.pay.paydaTam);

  return (
    <div className="space-y-2">
      {/* Seçim yalnızca dar ekranda anlamlı: genişte her zaman tablo. */}
      <div
        role="group"
        aria-label={M.rapor.tablo.gorunumGrubu}
        className="flex items-center gap-2 text-[12px] text-muted md:hidden"
      >
        <span aria-hidden="true">{M.rapor.tablo.gorunum}</span>
        <Button
          variant={gorunum === "tablo" ? "primary" : "ghost"}
          aria-pressed={gorunum === "tablo"}
          onClick={() => setGorunum("tablo")}
        >
          {M.rapor.tablo.tablo}
        </Button>
        <Button
          variant={gorunum === "kart" ? "primary" : "ghost"}
          aria-pressed={gorunum === "kart"}
          onClick={() => setGorunum("kart")}
        >
          {M.rapor.tablo.kart}
        </Button>
      </div>
      {satilmisSayisi > 0 && (
        // Düğme değil onay kutusu, cümlenin parçası (kullanıcı, 14.10.2026):
        // "Tamamı satılmış N varlık: [ ] Göster".
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted">
          <span>{M.rapor.tablo.satilmis(satilmisSayisi)}</span>
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-ink">
            <input
              type="checkbox"
              checked={satilmislariGoster}
              onChange={(e) => setSatilmislariGoster(e.currentTarget.checked)}
            />
            {M.rapor.tablo.goster}
          </label>
          <span>{M.rapor.tablo.toplamlarIcerir}</span>
        </p>
      )}
      {satirlar.length > 0 && (
        <div className="overflow-x-auto">
          {/* "Kart" seçiliyse dar ekranda her satır bir KART (bkz. global.css
              `.pf-table-kart`): başlık satırı gizlenir, her hücre kendi
              etiketini `data-etiket`ten yazar. Aynı DOM — ikinci tablo yok. */}
          <table className={cx("pf-table pf-card overflow-hidden", gorunum === "kart" && "pf-table-kart")} data-kart-bolum="liste">
            <caption className="sr-only">{M.rapor.tablo.baslik}</caption>
            <thead>
              <tr>
                <th scope="col" {...sira("varlik")}>
                  {/* Varlık'ta balon yok: başlığın kendisi sıralar. */}
                  <button
                    type="button"
                    aria-label={M.rapor.tablo.sirala(M.rapor.tablo.varlik)}
                    onClick={() => setSiralama((o) => sonrakiSiralama(o, "varlik"))}
                    className="inline-flex items-center gap-1 font-[inherit] text-[inherit]"
                  >
                    {M.rapor.tablo.varlik}
                    <SiraSimgesi durum={siralama.sutun === "varlik" ? siralama.yon : null} />
                  </button>
                </th>
                <th scope="col" className="pf-num">
                  {M.rapor.tablo.miktar}
                </th>
                <th scope="col" className="pf-num">
                  {M.rapor.tablo.birimFiyat}
                </th>
                <th scope="col" className="pf-num" {...sira("deger")}>
                  <FormulBasligi kod="piyasaDegeri" temettuHaric={temettuHaric}>
                    {deger}
                  </FormulBasligi>
                  {ok("deger", deger)}
                </th>
                <th scope="col" className="pf-num" {...sira("pay")}>
                  <PayBasligi eksik={paydaEksik} ok={ok("pay", M.rapor.tablo.pay)} />
                </th>
                <th scope="col" className="pf-num" {...sira("gunluk")}>
                  <FormulBasligi kod="gunlukGetiri">{M.rapor.tablo.gunluk}</FormulBasligi>
                  {ok("gunluk", M.rapor.tablo.gunluk)}
                </th>
                <th scope="col" className="pf-num" {...sira("maliyet")}>
                  <FormulBasligi kod="maliyet" temettuHaric={temettuHaric}>
                    {maliyet}
                  </FormulBasligi>
                  {ok("maliyet", maliyet)}
                </th>
                <th scope="col" className="pf-num" {...sira("mevcut")}>
                  <FormulBasligi kod="mevcutGetiri" temettuHaric={temettuHaric}>
                    {M.rapor.tablo.mevcutGetiri}
                  </FormulBasligi>
                  {ok("mevcut", M.rapor.tablo.mevcutGetiri)}
                </th>
                <th scope="col" className="pf-num">
                  <FormulBasligi kod="gerceklesmisKazanc" temettuHaric={temettuHaric}>
                    {M.rapor.tablo.gerceklesmis}
                  </FormulBasligi>
                </th>
                <th scope="col" className="pf-num">
                  <FormulBasligi kod="nakitAkisi" temettuHaric={temettuHaric}>
                    {M.rapor.tablo.nakitAkisi}
                  </FormulBasligi>
                </th>
                <th scope="col" className="pf-num">
                  <FormulBasligi kod="gerceklesmisGetiri" temettuHaric={temettuHaric}>
                    {M.rapor.tablo.gercGetiri}
                  </FormulBasligi>
                </th>
                <th scope="col" className="pf-num" {...sira("yillik")}>
                  <FormulBasligi kod="yillikKarsilik">{M.rapor.tablo.yillik}</FormulBasligi>
                  {ok("yillik", M.rapor.tablo.yillik)}
                  <span className="sr-only"> · </span>
                  <span className="block text-[10px] font-normal text-faint">{M.rapor.tablo.agrYas}</span>
                </th>
                <th scope="col">{M.rapor.tablo.damga}</th>
                <th scope="col">
                  <span className="sr-only">{M.ortak.ayrinti}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((p) => {
                const key = assetKey(p.asset);
                const open = acik === key;
                return (
                  <PozisyonSatiri
                    key={key}
                    p={p}
                    rowKey={key}
                    open={open}
                    onToggle={onToggle}
                    etiket={{ deger, maliyet }}
                    temettuHaric={temettuHaric}
                    kartGorunumu={gorunum === "kart"}
                    paydaEksik={paydaEksik}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Sıralama oku: seçili sütunda yön (↓ azalan, ↑ artan), öbürlerinde soluk ↕. */
function SiraSimgesi({ durum }: { readonly durum: "artan" | "azalan" | null }) {
  return (
    <svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true" className={durum === null ? "text-faint" : "text-ink"}>
      {durum !== "azalan" && <path d="M5 1.5 8 5H2z" fill="currentColor" />}
      {durum !== "artan" && <path d="M5 10.5 2 7h6z" fill="currentColor" />}
    </svg>
  );
}

/**
 * Formül balonlu başlığın yanındaki sıralama düğmesi: başlığın kendisi balonu
 * açar, ok sıralar (Gelen kararlar 28.10.2026).
 */
function SiraOku({
  ad,
  durum,
  onSirala,
}: {
  readonly ad: string;
  readonly durum: "artan" | "azalan" | null;
  readonly onSirala: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={M.rapor.tablo.sirala(ad)}
      onClick={onSirala}
      className="ml-0.5 inline-flex size-5 items-center justify-center rounded-[var(--pf-radius-sm)] align-middle hover:bg-surface-2"
    >
      <SiraSimgesi durum={durum} />
    </button>
  );
}

/**
 * PAY — seçili kapsam içinde, değere göre (kullanıcı kararı 15.10.2026).
 * Kapsam sayfadaki hesap seçicisi: bütün kasa, tek hesap ya da sanal hesap.
 * Oran motordan (binde, toplam tam %100); burada yalnızca biçim.
 */
/** Payda eksikse başlıkta ⓘ yerine uyarı; metni balonda formülün altında (20.10.2026). */
const PAYDA_EKSIK_UYARISI = M.rapor.tablo.paydaEksik;

function PayBasligi({ eksik, ok }: { readonly eksik: boolean; readonly ok?: ReactNode }) {
  return (
    <>
      <FormulBasligi kod="pay" {...(eksik ? { uyari: PAYDA_EKSIK_UYARISI } : {})}>
        {M.rapor.tablo.pay}
      </FormulBasligi>
      {ok}
      <span className="sr-only"> · </span>
      <span className="block text-[10px] font-normal text-faint">{M.rapor.tablo.seciliKapsamda}</span>
    </>
  );
}

function PayHucresi({ p }: { readonly p: PositionValuation }) {
  if (p.pay === null) return <Yokluk yokluk={p.absent.pay} />;
  return <span className="pf-fig">{M.bicim.yuzde(formatDecimal(p.pay.yuzde, { fractionDigits: 1, gizlenmez: true }))}</span>;
}

/** Birim fiyat — tabloda ve Ayrıntı panelinde AYNI bileşen; yoksa motorun sebebiyle "—". */
function BirimFiyat({ p }: { readonly p: PositionValuation }) {
  if (p.unitPrice === null) return <Yokluk yokluk={p.absent.unitPrice} />;
  // SEANS DIŞI (04.11.2026): fiyat seans öncesi/sonrası işlemse kısa etiket —
  // bilgi, anormallik değil (işaret ve yargı rengi yok).
  const seans = p.unitPrice.basis.find((b) => b.kind === "fiyat" && b.seans !== undefined)?.seans;
  return (
    <TutarTetik value={p.unitPrice} icerik={sorunSatiri(p.unitPrice)}>
      <NumberCell currency={p.unitPrice.currency}>
        {formatDecimal(p.unitPrice.amount, { maxFractionDigits: 8 })}
      </NumberCell>
      {seans !== undefined && (
        <span className="block text-[11px] text-muted" data-seans={seans}>
          {M.rapor.seans[seans]}
        </span>
      )}
    </TutarTetik>
  );
}

function PozisyonSatiri({
  p,
  rowKey,
  open,
  onToggle,
  etiket,
  temettuHaric = false,
  kartGorunumu = false,
  paydaEksik = false,
}: {
  readonly p: PositionValuation;
  readonly rowKey: string;
  readonly open: boolean;
  readonly onToggle: (key: string) => void;
  readonly etiket: { readonly deger: string; readonly maliyet: string };
  readonly temettuHaric?: boolean;
  readonly kartGorunumu?: boolean;
  /** Pay etiketinde ⓘ yerine uyarı (kart görünümünde başlık gizli). */
  readonly paydaEksik?: boolean;
}) {
  // Kart görünümünde etiket gerçek bir öğe: ⓘ ona bağlanır (sözde öğeye düğme
  // konamaz). Tablo görünümünde başlıkta zaten var; hücreye hiç eklenmez.
  const kartEtiketi = (ad: string, kod: DegerFormuluKodu) =>
    kartGorunumu ? (
      <span className="pf-kart-etiket">
        <FormulBasligi kod={kod} temettuHaric={temettuHaric} {...(kod === "pay" && paydaEksik ? { uyari: PAYDA_EKSIK_UYARISI } : {})}>
          {ad}
        </FormulBasligi>
      </span>
    ) : null;
  const ogeli = kartGorunumu ? "pf-num pf-etiket-ogeli" : "pf-num";
  const cf = p.cashFlow;
  return (
    <>
      <tr>
        <td className="pf-kart-baslik font-medium text-ink">
          {assetLabel(p.asset)}
          <span className="ml-1 text-[11px] text-faint">{p.currency}</span>
        </td>
        <td className="pf-num" data-etiket={M.rapor.tablo.miktar}>
          {formatDecimal(p.quantity, { maxFractionDigits: MIKTAR_HANE })}
        </td>
        <td className="pf-num" data-etiket={M.rapor.tablo.birimFiyat}>
          <BirimFiyat p={p} />
        </td>
        <td className={ogeli} data-etiket={etiket.deger}>
          {kartEtiketi(etiket.deger, "piyasaDegeri")}
          <Hucre value={p.reportValue} yokluk={p.absent.reportValue} formul={<DegerFormulu p={p} />} />
        </td>
        <td className={ogeli} data-etiket={M.rapor.tablo.paySeciliKapsamda}>
          {kartEtiketi(M.rapor.tablo.paySeciliKapsamda, "pay")}
          <PayHucresi p={p} />
        </td>
        <td className={ogeli} data-etiket={M.rapor.tablo.gunluk}>
          {kartEtiketi(M.rapor.tablo.gunluk, "gunlukGetiri")}
          <GunlukHucresi g={p.gunluk} yokluk={p.gunlukYok} />
        </td>
        <td className={ogeli} data-etiket={etiket.maliyet}>
          {kartEtiketi(etiket.maliyet, "maliyet")}
          <Hucre value={p.costConverted} yokluk={p.absent.costConverted} />
        </td>
        <td className={ogeli} data-etiket={M.rapor.tablo.mevcutGetiri}>
          {kartEtiketi(M.rapor.tablo.mevcutGetiri, "mevcutGetiri")}
          <Hucre
            value={p.unrealizedGain}
            yokluk={p.absent.unrealizedGain}
            signed
            ratio={p.unrealizedGainRatio}
            paydaAdi={CEPTEN}
            formul={
              <FormulSatiri
                terimler={[
                  { tutar: p.reportValue, ad: M.rapor.terim.deger },
                  { tutar: p.costConverted, ad: M.rapor.terim.maliyet, eksi: true },
                ]}
                sonuc={p.unrealizedGain}
              />
            }
          />
        </td>
        <td className={ogeli} data-etiket={M.rapor.tablo.gerceklesmis}>
          {kartEtiketi(M.rapor.tablo.gerceklesmis, "gerceklesmisKazanc")}
          <Hucre
            value={p.realizedGain}
            yokluk={p.absent.realizedGain}
            signed
            ratio={p.realizedGainRatio}
            paydaAdi={SATILAN}
          />
        </td>
        <td className={ogeli} data-etiket={M.rapor.tablo.nakitAkisi}>
          {kartEtiketi(M.rapor.tablo.nakitAkisi, "nakitAkisi")}
          <Hucre
            value={cf?.net ?? null}
            yokluk={p.absent.cashFlowNet}
            signed
            formul={<FormulSatiri terimler={nakitTerimleri(cf, temettuHaric)} sonuc={cf?.net ?? null} />}
          />
        </td>
        <td className={ogeli} data-etiket={M.rapor.tablo.gercGetiri}>
          {kartEtiketi(M.rapor.tablo.gercGetiri, "gerceklesmisGetiri")}
          <Hucre
            value={p.realizedReturn}
            yokluk={p.absent.realizedReturn}
            signed
            ratio={p.realizedReturnRatio}
            paydaAdi={SATILAN}
            formul={
              <FormulSatiri
                terimler={[{ tutar: p.realizedGain, ad: M.rapor.terim.satisKazanci }, ...nakitTerimleri(cf, temettuHaric)]}
                sonuc={p.realizedReturn}
              />
            }
          />
        </td>
        <td className={ogeli} data-etiket={M.rapor.tablo.yillikAgrYas}>
          {kartEtiketi(M.rapor.tablo.yillikAgrYas, "yillikKarsilik")}
          <YillikDeger
            k={p.yillikKarsilik}
            kapsam="varlik"
            geriYatirim={p.openLots.some((l) => l.temettuden === true) ? { tutar: null } : null}
          />
        </td>
        <td className="pf-kart-tam">
          <DamgaRozetleri notes={p.notes} />
        </td>
        <td className="pf-kart-tam">
          {/* Metin yerine ok (kullanıcı, 14.10.2026); açıkken yukarı döner. */}
          <button
            type="button"
            aria-expanded={open}
            aria-controls={`rapor-detay-${rowKey}`}
            aria-label={open ? M.rapor.tablo.gizle : M.ortak.ayrinti}
            title={open ? M.rapor.tablo.gizle : M.ortak.ayrinti}
            onClick={() => onToggle(rowKey)}
            className="inline-flex size-7 items-center justify-center rounded-[var(--pf-radius-sm)] text-muted hover:bg-surface-2 hover:text-ink"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              aria-hidden="true"
              className={cx("transition-transform", open && "rotate-180")}
            >
              <path d="M3 5.2 7 9.2l4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </td>
      </tr>
      {open && (
        <tr id={`rapor-detay-${rowKey}`}>
          <td colSpan={14} className="pf-kart-tam bg-surface-2 p-3">
            <PozisyonAyrintisi p={p} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * "—" VE SEBEBİ — kartta ve tabloda AYNI bileşen.
 *
 * İKİ GÖRÜNÜŞ, TEK KURAL: **ekranda görünen her işaret bir anormallik
 * demektir.**
 *
 * - `olculemedi` → uyarı rengi + kesikli alt çizgi. Bakılacak bir şey VAR.
 * - `yok` → soluk, işaretsiz. Satışı olmayan varlığın gerçekleşmiş kazancı
 *   gibi: defterin normal hâli. Sebep YİNE balonda — J'nin 1. kuralı iki
 *   durumda da geçerli, yalnızca işaret anormalliğe ayrılmış.
 *
 * `yokluk` gelmediyse motorun değişmezi bozulmuş demektir (testte kilitli).
 * Sessizce işaretsiz bir "—" göstermek o kusuru gizlerdi; bu yüzden en
 * yüksek sesle, anormallik olarak gösteriliyor.
 */
function Yokluk({
  yokluk,
  ek,
}: {
  readonly yokluk: Absence | undefined;
  readonly ek?: ReactNode;
}) {
  const olculemedi = yokluk === undefined || yokluk.kind === "olculemedi";
  return (
    <Aciklama
      className={olculemedi ? "pf-aciklama-isaretli text-warn" : "text-faint"}
      label={M.rapor.balon.neden}
      icerik={
        <>
          <span className="pf-aciklama-satir">
            {motorMetniVeya(yokluk?.reason, M.rapor.balon.motorSoylemedi)}
          </span>
          {ek}
        </>
      }
    >
      —
    </Aciklama>
  );
}

/**
 * YILLIK KARŞILIK (kullanıcı kararı 14.10.2026): basit yıllık oran ve
 * altında ağırlıklı yaş. Tanım ve getirinin kendisi balonda. Sayılar
 * MOTORUN (`YillikKarsilik`); burada hesap yok.
 */
function YillikDeger({
  k,
  buyuk = false,
  geriYatirim = null,
}: {
  readonly k: YillikKarsilik;
  readonly kapsam: "varlik" | "toplam";
  readonly buyuk?: boolean;
  /**
   * Geri yatırılan temettü (19.10.2026): paydaya ("cepten çıkan para")
   * GİRMEDİ. Toplamda motorun tutarı; varlıkta yalnızca var olduğu.
   */
  readonly geriYatirim?: { readonly tutar: StampedAmount | null } | null;
}) {
  // KISA (kullanıcı, 14.10.2026): tabloda etiket sütun başlığında ("ağr.
  // yaş"), satırda yalnızca "N gün"; kutuda kısa etiketiyle.
  const yas =
    k.yasGun === null ? null : (
      <span className={cx("block text-faint", buyuk ? "mt-1 text-[11px] font-normal" : "text-[11px]")}>
        {buyuk && M.rapor.yillik.agrYasOnEk}
        <span className="pf-num">{k.yasGun.toLocaleString(YEREL)}</span>
        {M.rapor.yillik.gunSonEk(k.yasGun)}
      </span>
    );
  if (k.yillik === null) {
    return (
      <>
        <Yokluk yokluk={k.absent.yillik} />
        {yas}
      </>
    );
  }
  return (
    <>
      <Aciklama
        className="pf-num"
        label={M.rapor.yillik.nasil}
        icerik={
          // Formül, bu değerlerle, iki satır (20.10.2026); tanım ⓘ'de.
          <>
            <span className="pf-aciklama-satir text-ink">
              <span className="pf-tek">{formatRatio(k.oran?.ratio)}</span>
              {M.rapor.yillik.getiriCarpi}
              <span className="pf-tek">{M.rapor.yillik.gun(k.yasGun?.toLocaleString(YEREL) ?? "", k.yasGun ?? 0)}</span> ={" "}
              <span className="pf-tek">{formatRatio(k.yillik)}</span>
            </span>
            {k.oran != null && (
              <span className="pf-aciklama-satir text-ink">
                {M.rapor.yillik.payda}
                <span className="pf-tek">{formatMoney(k.oran.base)} {k.oran.currency}</span>
                {M.rapor.yillik.cepten}
                {geriYatirim !== null && geriYatirim.tutar !== null && (
                  <>
                    {M.rapor.yillik.geriYatirilanOnEk}
                    <span className="pf-tek">
                      {formatMoney(geriYatirim.tutar.amount)} {geriYatirim.tutar.currency}
                    </span>
                    {M.rapor.yillik.geriYatirilanSonEk}
                  </>
                )}
              </span>
            )}
          </>
        }
      >
        <NumberCell sign={decimalSign(k.yillik)}>{formatRatio(k.yillik)}</NumberCell>
      </Aciklama>
      {yas}
    </>
  );
}

/**
 * TEK HÜCRE: sayı (oranıyla), ya da sebebiyle "—".
 *
 * Sayının kendisi balonun tetikleyicisi. Ayrı bir damga satırı YOK.
 *
 * N ŞERİDİ: "—" artık SEBEPSİZ DEĞİL. Önceki hâli düz bir `<span>`di —
 * özet kartları (L3) ve nakit tablosu (M) sebebini balonda söylerken
 * pozisyon tablosu J'nin 1. kuralını deliyordu. `yokluk` ZORUNLU bir alan:
 * yeni bir sütun eklenip sebebi verilmezse derleme düşer.
 */
/**
 * GÜNLÜK GETİRİ (Gelen kararlar 22.10.2026). Terimler motordan: bugünkü ve
 * önceki günün geçmiş dahil getirisi, fark, önceki günün değeri, kur etkisi.
 * Bayat fiyatta tutarın damgası bayattır: uyarı işareti ve "ne yapmalı"
 * balonda (`TutarTetik`), sayı yine gösterilir.
 */
function gunlukFormulu(g: GunlukGetiri): ReactNode {
  return (
    <>
      <FormulSatiri
        terimler={[
          { tutar: g.bugunGetiri, ad: g.sonHareketGunu ? M.rapor.terim.gunGetirisi(formatIsoDate(g.gun)) : M.rapor.terim.bugunkuGetiri },
          { tutar: g.oncekiGetiri, ad: M.rapor.terim.gunGetirisi(formatIsoDate(g.oncekiGun)), eksi: true },
        ]}
        sonuc={g.tutar}
      />
      {g.sonHareketGunu && (
        <span className="pf-aciklama-satir text-ink">
          {M.rapor.gunluk.sonGunAciklama(formatIsoDate(g.gun))}
        </span>
      )}
      {g.satirlarKarisik === true && (
        <span className="pf-aciklama-satir text-ink">
          {M.rapor.gunluk.satirlarKarisik}
        </span>
      )}
      {g.kurEtkisi !== null && (
        <span className="pf-aciklama-satir text-ink">
          {M.rapor.gunluk.kurEtkisi}
          <span className="pf-tek">
            {formatMoney(g.kurEtkisi.amount, { signed: true })} {g.kurEtkisi.currency}
          </span>
        </span>
      )}
    </>
  );
}

/** Hareketsiz günde gösterilen günün sade (yargısız) işareti: "· 23.10". */
function SonGunIsareti({ g }: { readonly g: GunlukGetiri }) {
  if (!g.sonHareketGunu) return null;
  const [, a, gun] = g.gun.split("-");
  return (
    <span className="ml-1 text-[10px] text-faint" title={M.rapor.gunluk.sonIslemGunu(formatIsoDate(g.gun))}>
      · {gun}.{a}
    </span>
  );
}

function GunlukHucresi({ g, yokluk }: { readonly g: GunlukGetiri | undefined; readonly yokluk: Absence | undefined }) {
  if (g === undefined) return <Yokluk yokluk={yokluk} />;
  return (
    <>
      <Hucre
        value={g.tutar}
        yokluk={undefined}
        signed
        ratio={g.oran}
        paydaAdi={M.rapor.terim.oncekiGunDegeri}
        formul={gunlukFormulu(g)}
      />
      <SonGunIsareti g={g} />
    </>
  );
}

function Hucre({
  value,
  yokluk,
  signed = false,
  ratio,
  formul,
  paydaAdi,
}: {
  readonly value: StampedAmount | null;
  readonly yokluk: Absence | undefined;
  readonly signed?: boolean;
  readonly ratio?: GainRatio | null;
  /** Formülün bu değerle yazılmış hâli (balonun ilk satırı). */
  readonly formul?: ReactNode;
  readonly paydaAdi?: string;
}) {
  if (value === null) return <Yokluk yokluk={yokluk} />;
  const bayat = bayatMi(value);
  return (
    <TutarTetik value={value} className="pf-num" icerik={tutarBalonu(value, formul, ratio, paydaAdi)}>
      <NumberCell {...(signed && !bayat ? { sign: decimalSign(value.amount) } : {})}>
        {formatMoney(value.amount, { signed })}
      </NumberCell>
      {ratio !== undefined && <OranMetni value={ratio} />}
    </TutarTetik>
  );
}

function PozisyonAyrintisi({ p }: { readonly p: PositionValuation }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">{M.rapor.ayrinti.birimFiyat}</p>
          <div className="text-[13px]">
            <BirimFiyat p={p} />
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
            {M.rapor.ayrinti.kesilenVergi}
          </p>
          {p.withheldTax === null ? (
            <p className="text-[12px] text-muted">{M.rapor.ayrinti.kesintiYok}</p>
          ) : (
            <div className="text-[13px]">
              <TutarTetik value={p.withheldTax} icerik={sorunSatiri(p.withheldTax)}>
                <NumberCell currency={p.withheldTax.currency}>
                  {formatMoney(p.withheldTax.amount)}
                </NumberCell>
              </TutarTetik>
            </div>
          )}
        </div>
      </div>

      <DayanakOzeti p={p} />

      <NakitAkisiDokumu cashFlow={p.cashFlow} />

      {p.openLots.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
            {M.rapor.ayrinti.acikPartiler}
          </p>
          <div className="overflow-x-auto">
            <table className="pf-table mt-1">
              <thead>
                <tr>
                  <th scope="col" className="pf-num">
                    {M.rapor.ayrinti.edinme}
                  </th>
                  <th scope="col" className="pf-num">
                    {M.rapor.ayrinti.miktar}
                  </th>
                  <th scope="col" className="pf-num">
                    {M.rapor.ayrinti.maliyet}
                  </th>
                  <th scope="col">{M.rapor.ayrinti.not}</th>
                </tr>
              </thead>
              <tbody>
                {p.openLots.map((lot) => (
                  <tr key={lot.id}>
                    <td className="pf-num text-muted">{formatIsoDate(lot.date)}</td>
                    <td className="pf-num">{formatDecimal(lot.quantity, { maxFractionDigits: MIKTAR_HANE })}</td>
                    <td className="pf-num">
                      {formatMoney(lot.cost)} <span className="text-[11px] text-faint">{lot.currency}</span>
                    </td>
                    <td>
                      {lot.costUnknown && (
                        <Tag className="text-warn">{M.rapor.ayrinti.maliyetBilinmiyor}</Tag>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {p.sales.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
            {M.rapor.ayrinti.satislar}
          </p>
          <ul className="mt-1 space-y-1 text-[12px]">
            {p.sales.map((s) => (
              <li key={s.id} className="border-l border-line pl-2">
                <span className="pf-num text-muted">{formatIsoDate(s.date)}</span> ·{" "}
                {TRANSACTION_TYPE_LABEL[s.type]} ·{" "}
                <span className="pf-num">{formatDecimal(s.quantity, { maxFractionDigits: MIKTAR_HANE })}</span>
                {M.rapor.ayrinti.adetHasilat}
                <span className="pf-num">{formatMoney(s.proceeds)}</span>
                {M.rapor.ayrinti.maliyetAra}
                <span className="pf-num">{formatMoney(s.cost)}</span>
                {M.rapor.ayrinti.kazancAra}
                <NumberCell sign={decimalSign(s.gain)} currency={s.currency}>
                  {formatMoney(s.gain, { signed: true })}
                </NumberCell>
                {s.costUnknown && <Tag className="ml-1 text-warn">{M.rapor.ayrinti.maliyetEksik}</Tag>}
                <ul className="mt-0.5 pl-3 text-[11px] text-muted">
                  {s.from.map((c, i) => (
                    <li key={`${c.lotId}|${i}`}>
                      <span className="pf-num">{formatIsoDate(c.lotDate)}</span>
                      {M.rapor.ayrinti.partisinden}
                      <span className="pf-num">
                        {formatDecimal(c.quantity, { maxFractionDigits: MIKTAR_HANE })}
                      </span>
                      {M.rapor.ayrinti.adetMaliyet}
                      <span className="pf-num">{formatMoney(c.cost)}</span>
                      {c.costUnknown && M.rapor.ayrinti.bilinmiyor}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.notes.length > 0 && <DamgaListesi notes={p.notes} title={M.rapor.damga.pozisyon} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nakit akışı dökümü — H1'in getirdiği ekran
// ---------------------------------------------------------------------------

function NakitAkisiDokumu({ cashFlow }: { readonly cashFlow: CashFlow | null }) {
  if (cashFlow === null) {
    return (
      <p className="text-[12px] text-muted">
        {M.rapor.nakitDokumu.yok}
      </p>
    );
  }
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
        {M.rapor.nakitDokumu.baslik}
      </p>
      <p className="mt-0.5 text-[12px]">
        {M.rapor.nakitDokumu.gelir}
        <SatirIci value={cashFlow.income} yokluk={cashFlow.absent.income} />
        {M.rapor.nakitDokumu.gider}
        <SatirIci value={cashFlow.expense} yokluk={cashFlow.absent.expense} />
        {M.rapor.nakitDokumu.net}
        <SatirIci value={cashFlow.net} yokluk={cashFlow.absent.net} />
      </p>
      <div className="overflow-x-auto">
        <table className="pf-table mt-1">
          <thead>
            <tr>
              <th scope="col" className="pf-num">
                {M.rapor.nakitDokumu.tarih}
              </th>
              <th scope="col">{M.rapor.nakitDokumu.tur}</th>
              <th scope="col" className="pf-num">
                {M.rapor.nakitDokumu.tutar}
              </th>
              <th scope="col" className="pf-num">
                {M.rapor.nakitDokumu.stopaj}
              </th>
              <th scope="col">{M.rapor.nakitDokumu.durum}</th>
            </tr>
          </thead>
          <tbody>
            {cashFlow.rows.map((row) => (
              <tr key={row.id} className={row.unreadable ? "text-faint" : undefined}>
                <td className="pf-num text-muted">{formatIsoDate(row.date)}</td>
                <td>{TRANSACTION_TYPE_LABEL[row.type]}</td>
                <td className="pf-num">
                  {/* Okunamayan satırın tutarı GÖSTERİLMİYOR: ekranda bir sayı
                      varsa toplama girmiş demektir; girmemiş bir sayıyı
                      göstermek "toplam neden tutmuyor" sorusunu doğurur. */}
                  {row.unreadable ? (
                    <span className="text-faint">{M.rapor.nakitDokumu.okunamadi}</span>
                  ) : (
                    <NumberCell sign={decimalSign(row.amount)} currency={row.currency}>
                      {formatMoney(row.amount, { signed: true })}
                    </NumberCell>
                  )}
                </td>
                <td className="pf-num text-muted">{formatMoney(row.withheldTax)}</td>
                <td>
                  {row.unreadable ? (
                    <Tag className="text-danger">{M.rapor.nakitDokumu.miktarYazilmamis}</Tag>
                  ) : (
                    <span className="text-[11px] text-faint">{M.rapor.nakitDokumu.toplamaGirdi}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
